import { __rest } from 'tslib';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import React, { useRef, useState, useEffect } from 'react';
import { CaptchaVersionEnum, ActionAfterSubmit, InputType, ContentType } from '@teko-builder/types/pb';
import { u as useForm } from './useForm-aa144cb0.js';
import styled from '@emotion/styled';
import { DEFAULT_FORM_STYLE, DEFAULT_OK_BUTTON } from './data.es.js';
import { getStyle } from './utils.es.js';
import { t as translations } from './translation-411fe6b6.js';
import { B as ButtonV1 } from './ButtonV2-6506d594.js';
import { T as Textarea, a as InputNumber, I as Input } from './InputNumber-7d93e963.js';
import './components/shared/Link.es.js';
import { S as Select, O as Option } from './Select-c8bcceda.js';
import './FormItem-098fa619.js';
import { DateTimePicker } from './components/shared/DateTimePicker.es.js';
import { R as RadioGroup } from './RadioGroup-4b4abf08.js';
import './Col-475038db.js';
import './Slider-93041044.js';
import './Container-ca9f82d8.js';
import './Divider-600cb92e.js';
import './Ripple-abf0f8a0.js';
import './Image-efa43cf5.js';
import { M as ModalFooter, a as Modal, S as SizeEnum, b as ModalHeader, c as ModalCloseIcon, d as ModalBody } from './ModalHeader-a3d30e4f.js';
import './Spin-a6892495.js';
import ReCAPTCHA from 'react-google-recaptcha';
import { useGoogleReCaptcha, GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { logger, Api, PATTERN_VALIDATION as PATTERN_VALIDATION$1 } from '@teko-builder/utils';
import { b as getParamsFromUrl } from './common-dbaafe9f.js';
import 'rc-select';
import '@teko-builder/icons';
import './DateTimePickerMobile-749dc497.js';
import { useForm as useForm$1 } from 'react-hook-form';
import get from 'lodash.get';
import { GoogleSpreadsheet } from 'google-spreadsheet';
import classNames from 'classnames';

const StyledForm = styled.form(({ commonStyle }) => `
  width: 100%;
  z-index: 1;
  ${getStyle(commonStyle)}
  display: flex;
  flex-wrap: wrap;
`);
const StyledFormItem = styled.div(({ width, rowGap, columnGap }) => {
    if (!width || typeof width !== 'number') {
        width = DEFAULT_FORM_STYLE.formItem.columnWidth;
    }
    if (rowGap !== 0 && (!rowGap || typeof rowGap !== 'number')) {
        rowGap = DEFAULT_FORM_STYLE.rowGap;
    }
    if (columnGap !== 0 && (!columnGap || typeof columnGap !== 'number')) {
        columnGap = DEFAULT_FORM_STYLE.columnGap;
    }
    return `
    width: ${width}%;
    padding: 0 ${columnGap / 2}px;
    margin-bottom: ${rowGap}px;

    &.has-error {
      margin-bottom: ${rowGap >= 24 ? rowGap - 20 : 4}px;
    }

    .error {
      padding-top: 4px;
      line-height: 16px;
      transition: color 0.3s cubic-bezier(0.215, 0.61, 0.355, 1);
    }
  `;
});
const StyledLabel$3 = styled.label(({ color }) => `
  position: relative;
  margin-bottom: 8px;
  display: inline-flex;
  align-items: center;
  color: ${color ? color : 'unset'};
  &.required {
    &:before {
      display: inline-block;
      margin-right: 4px;
      color: #ff4d4f;
      font-size: 14px;
      font-family: SimSun, sans-serif;
      line-height: 1;
      content: '*';
    }
  }
`);

const useCaptcha = (setting) => {
    const showCapchaV2 = (setting === null || setting === void 0 ? void 0 : setting.show) &&
        (setting === null || setting === void 0 ? void 0 : setting.siteKey) &&
        [CaptchaVersionEnum.checkboxV2, CaptchaVersionEnum.invisibleV2].includes(setting === null || setting === void 0 ? void 0 : setting.version);
    const showCapchaV3 = (setting === null || setting === void 0 ? void 0 : setting.show) &&
        (setting === null || setting === void 0 ? void 0 : setting.siteKey) &&
        (setting === null || setting === void 0 ? void 0 : setting.version) === CaptchaVersionEnum.invisibleV3;
    return {
        showCapchaV2,
        showCapchaV3,
    };
};

const useCaptchaV2 = (setting, onSubmit) => {
    const recaptchaRef = useRef();
    const [requiredCheckRobot, setRequiredCheckRobot] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const onSubmitWithCaptcha = typeof onSubmit === 'function'
        ? async (values) => {
            const recaptchaValue = recaptchaRef.current.getValue();
            if (setting.version === CaptchaVersionEnum.checkboxV2) {
                if (recaptchaValue) {
                    setRequiredCheckRobot(false);
                    try {
                        setSubmitting(true);
                        await onSubmit(values);
                    }
                    finally {
                        setSubmitting(false);
                    }
                }
                else {
                    setRequiredCheckRobot(true);
                }
                return;
            }
            try {
                setSubmitting(true);
                await recaptchaRef.current.executeAsync();
                await onSubmit(values);
            }
            catch (e) {
                console.error(e);
            }
            finally {
                setSubmitting(false);
            }
        }
        : undefined;
    return {
        onSubmitWithCaptcha,
        recaptchaRef,
        requiredCheckRobot,
        setRequiredCheckRobot,
        submitting,
    };
};

const useCaptchaV3 = (setting, onSubmit) => {
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [submitting, setSubmitting] = useState(false);
    const onSubmitWithCaptcha = typeof onSubmit === 'function'
        ? async (values) => {
            if (typeof onSubmit === 'function') {
                if (!executeRecaptcha) {
                    console.log('Execute recaptcha not yet available');
                    return;
                }
                try {
                    setSubmitting(true);
                    await executeRecaptcha(setting.action || undefined);
                    await onSubmit(values);
                }
                catch (e) {
                    console.error(e);
                }
                finally {
                    setSubmitting(false);
                }
            }
        }
        : undefined;
    return {
        onSubmitWithCaptcha,
        submitting,
    };
};

const useNoCaptcha = (onSubmit) => {
    const [submitting, setSubmitting] = useState(false);
    const onSubmitWithNoCaptcha = typeof onSubmit === 'function'
        ? async (values) => {
            if (typeof onSubmit === 'function') {
                try {
                    setSubmitting(true);
                    await onSubmit(values);
                }
                catch (e) {
                    console.error(e);
                }
                finally {
                    setSubmitting(false);
                }
            }
        }
        : undefined;
    return {
        onSubmitWithNoCaptcha,
        submitting,
    };
};

class FormUtil {
    constructor() {
        this.submitByApi = async (url, body) => {
            try {
                const response = await this.api.post(url, body);
                return response;
            }
            catch (err) {
                logger.error(err);
                return err;
            }
        };
        this.insertDataToGoogleSheet = async (url, body, auth) => {
            var _a;
            try {
                const doc_id = ((_a = url.match(/[-\w]{25,}/)) === null || _a === void 0 ? void 0 : _a.toString()) || '';
                const doc = new GoogleSpreadsheet(doc_id);
                await doc.useServiceAccountAuth({
                    client_email: auth.clientEmail,
                    private_key: auth.privateKey,
                });
                await doc.loadInfo();
                const sheet1 = doc.sheetsByIndex[0];
                let headers = null;
                try {
                    await sheet1.loadHeaderRow();
                    headers = sheet1.headerValues;
                }
                catch (e) {
                    console.error(e);
                }
                if (!headers) {
                    await sheet1.setHeaderRow(Object.keys(body));
                }
                else {
                    const newHeaders = Object.keys(body).filter((el) => !headers.includes(el));
                    await sheet1.setHeaderRow([...headers, ...newHeaders]);
                }
                const validData = Object.values(body).filter(Boolean).length >= 1;
                if (validData) {
                    const row = await sheet1.addRow(body);
                    await row.save();
                    return { code: 200, data: body };
                }
            }
            catch (err) {
                logger.error(err);
                return { code: 404 };
            }
        };
        this.api = new Api('');
    }
}
const form = new FormUtil();

const defaultErrorPopup = {
    title: 'Thông báo',
    content: 'Có lỗi xảy ra từ hệ thống, vui lòng thử lại sau',
    showCloseBtn: true,
    popupButton: [Object.assign(Object.assign({}, DEFAULT_OK_BUTTON), { text: 'OK' })],
};
const defaultGGSheetKey$2 = {
    clientEmail: 'page-builder@pagebuilder-325814.iam.gserviceaccount.com',
    privateKey: '-----BEGIN PRIVATE KEY-----\nMIIEvAIBADANBgkqhkiG9w0BAQEFAASCBKYwggSiAgEAAoIBAQDfgDUM0KqKydMq\nlrB41+o/no2Jd+ah5bLbyez1S4Nsiz8JZ/M4iQlje+YQucIRgLP2dzRXHbTgELPl\nSoXNHHDpUks/jhngYODKBNPzGi6yQ4sCFWF+r0kDgeSP0Bf3QuVZGyDrKmWHovRx\naxKgLmMoz8EoG4056pD5GuS/5N5UgqmYevBDS/775eemmUSfmzig8mUScJnNks18\nkuqP3txhbzCOTGaRa447Vn2OxatGCyDuui+jTbbmhGLD6wWz/gB8hwdTzVx5AgWJ\nLN1Ho9YtFwJvqG+HrcQoYM8548Zx9pvYHAZo6m0lgY6TqN+Q79K5J5Z63PRZ4EuP\nAZN53mV1AgMBAAECggEAHBAAOTdzDmGYfMq2ZjnwDORmz5mpMO9DJaMlOEhuvECA\nnfj6GkFklyPtp3B3fFSdZAckIIMXnKGkQNg6VB5r0/P0BEselyR63Qq9pRLGxbXC\n3LRcDTjLkV/sF52QgMiavPUxzS1B2cW9bQc9a7TfWPa7AULyWNEJumlkyu5c9x3D\nuHuThjJgmMNZR8Yd5e4nguySfTtTUoXBPuDBH9bOmUWhkjT5sqSlinEUo7Hdmde+\nHTrqZAyMR4r5q5iYyECSXAFINeKrG75uEegNRTX+0ulg8zXn91vOvfJLOe1qGNrC\njOMlW2GtKEEaPcfNEu9WmnPtKsijNTbA9MLvcaKZ+QKBgQD5VV8HFxhHgoKIWapG\nuY0tYW5swcjBDXSDy90ZSm6HEBIOu7/Q9z188O0KZ/RsA8uKTwKTh2dQnf37fs4t\nFBkL7gmU/os4I1E6+KS95md4HJB+zATjWg4zLH2oRO3b+wlhhIVcuf6weFtKWg0C\nyyMMz4rl5q1SrUyBhUm/rVkd/QKBgQDlegR6B6h5CffEh7JUMJQJ1IQuw7E3jnS0\nTTYV8dLom5QP/lHxtXP9hGneBeEIwyrI3D3c9bTAq/4iEAWuM9I9KH9yT/E3UpYM\nw9BBIGC6B6cVV5gH/VDak1bum+cRUiabMSqfzgu5oTPol8df9z6wx42Q/MkRh07l\ny7fmEvgC2QKBgDqHJ79qVHC02h9XrPjdJZ65eEpfUti+Z9NoT4ZYSom55ANBX02W\nPADQLVR8Aow4d9cHpPsslbv+2lfm9E7woNCEGAu5lMSgxbiqFUHei/tN+T1KdvtH\ncH3iu7x8MbmpvBIAIa4amwahugxCBzvYo2mmblx5QcZel87vkthDwN+hAoGAE8JE\n3mkIZZupHN/+R+eC57HHy+sHuFvoypmXXZ5b5bF3t0TTs3dHTH35S6e/76w2Y+Mq\n5Hq3nJtyn2Y3xv8pTCDPkH03rObMir7VkLYD/iCoj1rieVWjf56OLq933vLu5g/+\ntpHcSpiJyq4++ZFlcX1NQD2z5nF7Jqg65nGdr0kCgYB23jyA0/ZJ9ohv5aUrucQZ\ndkFgs/o4F6PMx+Ro40DItCERpS1OeI1gAfy8u9SOEGDBcNDfPJZHzDn3bXQ0F6DI\nEqmwnESRAOtApHIbIRRud3sFJNBV0eWc3WFe7xLi4ex3EvAKoBkxe12j2k8zAJMs\nLms3WovJ2talTXcDHSblqA==\n-----END PRIVATE KEY-----\n',
};
const replaceContentApi$2 = (text, replaceData) => {
    const fieldNeedReplace = text.match(/{{(\w*)}}/g);
    fieldNeedReplace === null || fieldNeedReplace === void 0 ? void 0 : fieldNeedReplace.forEach((field) => {
        var _a;
        const apiField = (_a = field.match(/{{(\w*)}}/)) === null || _a === void 0 ? void 0 : _a[1];
        const fieldResponse = apiField ? replaceData[apiField] : '';
        text = text.replace(field, fieldResponse);
    });
    return text;
};
const useFormPopup = (formConfig) => {
    const [popupConfig, setPopupConfig] = useState();
    const [show, setShow] = useState(true);
    const toggle = () => setShow(false);
    const handleSubmitCallApi = async (values) => {
        var _a, _b, _c;
        try {
            const url = ((_b = (_a = formConfig === null || formConfig === void 0 ? void 0 : formConfig.settingAfterSubmit) === null || _a === void 0 ? void 0 : _a.api) === null || _b === void 0 ? void 0 : _b.url) || '';
            let urlParams = {};
            if (formConfig.submitWithParams) {
                urlParams = getParamsFromUrl(window.location.search);
            }
            const response = await form.submitByApi(url, Object.assign(Object.assign({}, values), urlParams));
            const { code: httpCode } = response;
            const formConfiguredPopup = formConfig === null || formConfig === void 0 ? void 0 : formConfig.responsePopup;
            let popupByCode;
            let apiData = {};
            if (httpCode >= 200 && httpCode <= 299) {
                popupByCode = formConfiguredPopup === null || formConfiguredPopup === void 0 ? void 0 : formConfiguredPopup.find((e) => { var _a, _b; return e.code === ((_b = (_a = response.data) === null || _a === void 0 ? void 0 : _a.code) === null || _b === void 0 ? void 0 : _b.toString()); });
                apiData = (_c = response === null || response === void 0 ? void 0 : response.data) === null || _c === void 0 ? void 0 : _c.data;
            }
            else {
                popupByCode = formConfiguredPopup === null || formConfiguredPopup === void 0 ? void 0 : formConfiguredPopup.find((e) => { var _a, _b; return e.code === ((_b = (_a = response.response) === null || _a === void 0 ? void 0 : _a.code) === null || _b === void 0 ? void 0 : _b.toString()); });
                apiData = response === null || response === void 0 ? void 0 : response.response;
            }
            let popupConfig;
            if (popupByCode) {
                popupConfig = popupByCode.popup;
            }
            else if (!(httpCode >= 200 && httpCode <= 299)) {
                popupConfig = defaultErrorPopup;
            }
            if (!popupConfig)
                return;
            popupConfig.content = replaceContentApi$2(popupConfig.content, apiData);
            setPopupConfig(popupConfig);
            setShow(true);
        }
        catch (e) {
            logger.error(e);
        }
    };
    const handleSubmitInsertGGSheet = async (values) => {
        var _a, _b;
        try {
            const url = ((_b = (_a = formConfig.settingAfterSubmit) === null || _a === void 0 ? void 0 : _a.sheet) === null || _b === void 0 ? void 0 : _b.url) || '';
            const ggSheetKey = formConfig.ggSheetKey;
            let urlParams = {};
            if (formConfig.submitWithParams) {
                urlParams = getParamsFromUrl(window.location.search);
            }
            const response = await form.insertDataToGoogleSheet(url, Object.assign(Object.assign({}, values), urlParams), Object.assign(Object.assign({}, defaultGGSheetKey$2), ggSheetKey));
            const { code, data } = response;
            const successPopup = formConfig === null || formConfig === void 0 ? void 0 : formConfig.sheetResponsePopup;
            let popupConfig;
            if (code === 200 && successPopup) {
                popupConfig = successPopup;
            }
            else if (code === 404) {
                popupConfig = defaultErrorPopup;
            }
            if (!popupConfig)
                return;
            popupConfig.content = replaceContentApi$2(popupConfig.content, data);
            setPopupConfig(popupConfig);
            setShow(true);
        }
        catch (e) {
            logger.error(e);
        }
    };
    const onSubmitWrapper = (values) => {
        var _a, _b;
        if (formConfig === null || formConfig === void 0 ? void 0 : formConfig.isEditorMode) {
            formConfig === null || formConfig === void 0 ? void 0 : formConfig.onSubmit(values);
            return;
        }
        if ((_a = formConfig === null || formConfig === void 0 ? void 0 : formConfig.actionAfterSubmit) === null || _a === void 0 ? void 0 : _a.includes(ActionAfterSubmit.api)) {
            handleSubmitCallApi(values);
            return;
        }
        if ((_b = formConfig === null || formConfig === void 0 ? void 0 : formConfig.actionAfterSubmit) === null || _b === void 0 ? void 0 : _b.includes(ActionAfterSubmit.sheet)) {
            handleSubmitInsertGGSheet(values);
            return;
        }
    };
    return {
        show,
        toggle,
        onSubmitWrapper,
        popupConfig,
    };
};

const FormPopup = (_a) => {
    var { show, toggle, commonStyle, customTitle, title, customContent, content, parent, children, popupButton, showCloseBtn, className } = _a, rest = __rest(_a, ["show", "toggle", "commonStyle", "customTitle", "title", "customContent", "content", "parent", "children", "popupButton", "showCloseBtn", "className"]);
    const onClickBtn = (button) => {
        if (button.hideAfterClick) {
            toggle();
        }
    };
    return (jsx(Wrapper$8, Object.assign({ commonStyle: commonStyle }, rest, { children: jsxs(Modal, Object.assign({ parent: parent, size: SizeEnum.s, show: show, className: className }, { children: [jsx(ModalHeader, { children: customTitle || jsx("div", { dangerouslySetInnerHTML: { __html: title } }) }), showCloseBtn && jsx(ModalCloseIcon, { onHide: toggle }), jsx(ModalBody, { children: customContent || (jsx("div", { dangerouslySetInnerHTML: { __html: content } })) }), jsx(StyledModalFooter, { children: popupButton === null || popupButton === void 0 ? void 0 : popupButton.map((btn, index) => (jsx(StyledPopupButton, Object.assign({}, btn, { onClick: () => onClickBtn(btn) }), index))) }), children] })) })));
};
const Wrapper$8 = styled.div(({ commonStyle }) => `
  ${getStyle(commonStyle)};
`);
const StyledModalFooter = styled(ModalFooter) `
  column-gap: 16px;
`;
const StyledPopupButton = styled(ButtonV1) `
  width: auto;
`;

const PATTERN_VALIDATION = {
    email: /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i,
    phone: /^([+84|84|0]+(3|5|7|8|9|1[2|6|8|9]))+([0-9]{8})\b$/,
    vietnameseName: /^[a-zA-ZÀÁÂÃÈÉÊÌÍÒÓÔÕÙÚĂĐĨŨƠàáâãèéêìíòóôõùúăđĩũơƯĂẠẢẤẦẨẪẬẮẰẲẴẶẸẺẼỀỀỂẾưăạảấầẩẫậắằẳẵặẹẻẽềềểếỄỆỈỊỌỎỐỒỔỖỘỚỜỞỠỢỤỦỨỪễệỉịọỏốồổỗộớờởỡợụủứừỬỮỰỲỴÝỶỸửữựỳỵýỷỹ\s\w|_]+$/,
};
const getDataHidden = (formItems) => {
    const hiddenFields = formItems.filter((field) => field.type === InputType.hidden);
    return hiddenFields.reduce((acc, curr) => {
        var _a, _b;
        return Object.assign(Object.assign({}, acc), { [curr.name || 'hidden']: (_b = (_a = curr === null || curr === void 0 ? void 0 : curr.additionOptions) === null || _a === void 0 ? void 0 : _a.hidden) === null || _b === void 0 ? void 0 : _b.defaultValue });
    }, {});
};
const getConfigValidations = (formItems) => {
    const displayFields = formItems.filter((f) => f.type !== InputType.hidden);
    return displayFields.reduce((acc, curr) => {
        const config = {};
        if (curr.required) {
            config.required = {
                value: true,
                message: translations.IsRequired,
            };
        }
        if (curr.type === InputType.email) {
            config.pattern = {
                value: PATTERN_VALIDATION.email,
                message: translations.PleaseEnterValidEmail,
            };
        }
        if (curr.type === InputType.phoneNumber) {
            config.pattern = {
                value: PATTERN_VALIDATION.phone,
                message: translations.PleaseEnterValidPhone,
            };
        }
        return Object.assign(Object.assign({}, acc), { [curr.name]: config });
    }, {});
};
const BasicForm = React.forwardRef(({ id, name, formItems, rowGap, columnGap, commonStyle, submitButton, onSubmit, errorColor, labelColor, showLabel, inputSize, className, style, onClickFormItem, onClickFormItemSubmit, renderCaptcha, submitting, }, ref) => {
    const { data: values, errors, handleChange, handleSubmit, } = useForm({
        initialValues: getDataHidden(formItems),
        validations: getConfigValidations(formItems),
        onSubmit,
    });
    const renderButton = () => {
        return (jsx(StyledFormItem, Object.assign({ onClick: (e) => {
                e.preventDefault();
                onClickFormItemSubmit === null || onClickFormItemSubmit === void 0 ? void 0 : onClickFormItemSubmit();
            }, rowGap: rowGap, columnGap: columnGap, style: {
                display: 'flex',
            } }, { children: jsx(ButtonV1, Object.assign({}, submitButton, { onClick: handleSubmit, disabled: submitting })) })));
    };
    return (jsxs(StyledForm, Object.assign({ ref: ref, style: style, id: id, commonStyle: commonStyle, name: name, className: className }, { children: [formItems.map((formItem, index) => {
                const className = formItem.required ? 'required' : '';
                const renderInput = () => {
                    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t;
                    switch (formItem.type) {
                        case InputType.text:
                        case InputType.phoneNumber:
                        case InputType.email:
                            return (jsx(Input, { value: values[formItem.name], size: inputSize, placeholder: formItem.placeholder, onChange: (e) => handleChange(formItem.name, e.target.value) }));
                        case InputType.number:
                            return (jsx(InputNumber, { value: values[formItem.name], size: inputSize, placeholder: formItem.placeholder, min: (_b = (_a = formItem.additionOptions) === null || _a === void 0 ? void 0 : _a.number) === null || _b === void 0 ? void 0 : _b.min, max: (_d = (_c = formItem.additionOptions) === null || _c === void 0 ? void 0 : _c.number) === null || _d === void 0 ? void 0 : _d.max, onChange: (val) => handleChange(formItem.name, val) }));
                        case InputType.textarea:
                            return (jsx(Textarea, { value: values[formItem.name], size: inputSize, placeholder: formItem.placeholder, rows: (_f = (_e = formItem.additionOptions) === null || _e === void 0 ? void 0 : _e.textarea) === null || _f === void 0 ? void 0 : _f.rows, onChange: (e) => handleChange(formItem.name, e.target.value) }));
                        case InputType.select:
                            return (jsx(Select, Object.assign({ value: values[formItem.name], style: { width: '100%' }, size: inputSize, placeholder: formItem.placeholder, mode: ((_h = (_g = formItem.additionOptions) === null || _g === void 0 ? void 0 : _g.select) === null || _h === void 0 ? void 0 : _h.multiple)
                                    ? 'multiple'
                                    : undefined, allowClear: (_k = (_j = formItem.additionOptions) === null || _j === void 0 ? void 0 : _j.select) === null || _k === void 0 ? void 0 : _k.allowClear, onChange: (val) => handleChange(formItem.name, val) }, { children: (((_m = (_l = formItem.additionOptions) === null || _l === void 0 ? void 0 : _l.select) === null || _m === void 0 ? void 0 : _m.options) || []).map((opt, index) => (jsx(Option, Object.assign({ value: opt.value }, { children: opt.label }), index))) })));
                        case InputType.dateTime:
                            return (jsx(DateTimePicker, { type: (_p = (_o = formItem.additionOptions) === null || _o === void 0 ? void 0 : _o.dateTime) === null || _p === void 0 ? void 0 : _p.type, value: values[formItem.name], size: inputSize, placeholder: formItem.placeholder, onChange: (val) => handleChange(formItem.name, val), style: { width: '100%' } }));
                        case InputType.radioGroup:
                            return (jsx(RadioGroup, { value: values[formItem.name], onChange: (val) => handleChange(formItem.name, val), options: (_r = (_q = formItem.additionOptions) === null || _q === void 0 ? void 0 : _q.radioGroup) === null || _r === void 0 ? void 0 : _r.options, inline: (_t = (_s = formItem.additionOptions) === null || _s === void 0 ? void 0 : _s.radioGroup) === null || _t === void 0 ? void 0 : _t.inline }));
                        default:
                            return null;
                    }
                };
                if (renderInput())
                    return (jsxs(StyledFormItem, Object.assign({ onClick: () => onClickFormItem === null || onClickFormItem === void 0 ? void 0 : onClickFormItem(index), width: formItem.columnWidth, rowGap: rowGap, columnGap: columnGap, className: !!errors[formItem.name] ? 'has-error' : '' }, { children: [showLabel && (jsxs(StyledLabel$3, Object.assign({ className: className, color: labelColor }, { children: [formItem.label, formItem.label ? ':' : ''] }))), renderInput(), !!errors[formItem.name] && (jsx("div", Object.assign({ style: { color: errorColor ? errorColor : 'unset' }, className: "error" }, { children: errors[formItem.name] })))] }), index));
                return null;
            }), renderCaptcha, renderButton()] })));
});
const FormCaptchaV2 = React.forwardRef((props, ref) => {
    const { captcha, onSubmit } = props, rest = __rest(props, ["captcha", "onSubmit"]);
    const { recaptchaRef, onSubmitWithCaptcha, requiredCheckRobot, setRequiredCheckRobot, submitting, } = useCaptchaV2(captcha, onSubmit);
    return (jsx(BasicForm, Object.assign({ ref: ref }, rest, { onSubmit: onSubmitWithCaptcha, submitting: submitting, renderCaptcha: captcha.version === CaptchaVersionEnum.checkboxV2 ? (jsxs(StyledFormItem, Object.assign({ width: 100, rowGap: rest === null || rest === void 0 ? void 0 : rest.rowGap, columnGap: rest === null || rest === void 0 ? void 0 : rest.columnGap }, { children: [jsx(ReCAPTCHA, { ref: recaptchaRef, sitekey: captcha.siteKey, onChange: () => setRequiredCheckRobot(false) }), requiredCheckRobot && (jsx("div", Object.assign({ style: { color: rest.errorColor ? rest.errorColor : 'unset' }, className: "error" }, { children: "Vui l\u00F2ng x\u00E1c minh danh t\u00EDnh" })))] }))) : (jsx(ReCAPTCHA, { ref: recaptchaRef, sitekey: captcha.siteKey, size: "invisible" })) })));
});
const FormCaptchaV3 = React.forwardRef((props, ref) => {
    const { captcha, onSubmit } = props, rest = __rest(props, ["captcha", "onSubmit"]);
    const { onSubmitWithCaptcha, submitting } = useCaptchaV3(captcha, onSubmit);
    return (jsx(BasicForm, Object.assign({ ref: ref }, rest, { onSubmit: onSubmitWithCaptcha, submitting: submitting })));
});
const Form$2 = React.forwardRef((props, ref) => {
    const { captcha, isEditorMode } = props;
    const { showCapchaV2, showCapchaV3 } = useCaptcha(captcha);
    const { onSubmitWrapper, popupConfig, show, toggle } = useFormPopup(props);
    const { onSubmitWithNoCaptcha, submitting } = useNoCaptcha(onSubmitWrapper);
    const renderForm = () => {
        if (showCapchaV2) {
            return (jsx(FormCaptchaV2, Object.assign({ ref: ref }, props, { onSubmit: onSubmitWrapper })));
        }
        if (showCapchaV3) {
            return (jsx(GoogleReCaptchaProvider, Object.assign({ reCaptchaKey: captcha.siteKey }, { children: jsx(FormCaptchaV3, Object.assign({ ref: ref }, props, { onSubmit: onSubmitWrapper })) })));
        }
        return (jsx(BasicForm, Object.assign({ ref: ref }, props, { onSubmit: onSubmitWithNoCaptcha, submitting: submitting })));
    };
    return (jsxs(Fragment, { children: [renderForm(), !isEditorMode && !!popupConfig && (jsx(FormPopup, Object.assign({}, popupConfig, { show: show, toggle: toggle })))] }));
});
Form$2.displayName = 'Form';

const api$1 = new Api('');
const defaultGGSheetKey$1 = {
    clientEmail: 'page-builder@pagebuilder-325814.iam.gserviceaccount.com',
    privateKey: '-----BEGIN PRIVATE KEY-----\nMIIEvAIBADANBgkqhkiG9w0BAQEFAASCBKYwggSiAgEAAoIBAQDfgDUM0KqKydMq\nlrB41+o/no2Jd+ah5bLbyez1S4Nsiz8JZ/M4iQlje+YQucIRgLP2dzRXHbTgELPl\nSoXNHHDpUks/jhngYODKBNPzGi6yQ4sCFWF+r0kDgeSP0Bf3QuVZGyDrKmWHovRx\naxKgLmMoz8EoG4056pD5GuS/5N5UgqmYevBDS/775eemmUSfmzig8mUScJnNks18\nkuqP3txhbzCOTGaRa447Vn2OxatGCyDuui+jTbbmhGLD6wWz/gB8hwdTzVx5AgWJ\nLN1Ho9YtFwJvqG+HrcQoYM8548Zx9pvYHAZo6m0lgY6TqN+Q79K5J5Z63PRZ4EuP\nAZN53mV1AgMBAAECggEAHBAAOTdzDmGYfMq2ZjnwDORmz5mpMO9DJaMlOEhuvECA\nnfj6GkFklyPtp3B3fFSdZAckIIMXnKGkQNg6VB5r0/P0BEselyR63Qq9pRLGxbXC\n3LRcDTjLkV/sF52QgMiavPUxzS1B2cW9bQc9a7TfWPa7AULyWNEJumlkyu5c9x3D\nuHuThjJgmMNZR8Yd5e4nguySfTtTUoXBPuDBH9bOmUWhkjT5sqSlinEUo7Hdmde+\nHTrqZAyMR4r5q5iYyECSXAFINeKrG75uEegNRTX+0ulg8zXn91vOvfJLOe1qGNrC\njOMlW2GtKEEaPcfNEu9WmnPtKsijNTbA9MLvcaKZ+QKBgQD5VV8HFxhHgoKIWapG\nuY0tYW5swcjBDXSDy90ZSm6HEBIOu7/Q9z188O0KZ/RsA8uKTwKTh2dQnf37fs4t\nFBkL7gmU/os4I1E6+KS95md4HJB+zATjWg4zLH2oRO3b+wlhhIVcuf6weFtKWg0C\nyyMMz4rl5q1SrUyBhUm/rVkd/QKBgQDlegR6B6h5CffEh7JUMJQJ1IQuw7E3jnS0\nTTYV8dLom5QP/lHxtXP9hGneBeEIwyrI3D3c9bTAq/4iEAWuM9I9KH9yT/E3UpYM\nw9BBIGC6B6cVV5gH/VDak1bum+cRUiabMSqfzgu5oTPol8df9z6wx42Q/MkRh07l\ny7fmEvgC2QKBgDqHJ79qVHC02h9XrPjdJZ65eEpfUti+Z9NoT4ZYSom55ANBX02W\nPADQLVR8Aow4d9cHpPsslbv+2lfm9E7woNCEGAu5lMSgxbiqFUHei/tN+T1KdvtH\ncH3iu7x8MbmpvBIAIa4amwahugxCBzvYo2mmblx5QcZel87vkthDwN+hAoGAE8JE\n3mkIZZupHN/+R+eC57HHy+sHuFvoypmXXZ5b5bF3t0TTs3dHTH35S6e/76w2Y+Mq\n5Hq3nJtyn2Y3xv8pTCDPkH03rObMir7VkLYD/iCoj1rieVWjf56OLq933vLu5g/+\ntpHcSpiJyq4++ZFlcX1NQD2z5nF7Jqg65nGdr0kCgYB23jyA0/ZJ9ohv5aUrucQZ\ndkFgs/o4F6PMx+Ro40DItCERpS1OeI1gAfy8u9SOEGDBcNDfPJZHzDn3bXQ0F6DI\nEqmwnESRAOtApHIbIRRud3sFJNBV0eWc3WFe7xLi4ex3EvAKoBkxe12j2k8zAJMs\nLms3WovJ2talTXcDHSblqA==\n-----END PRIVATE KEY-----\n',
};
const openPopup$1 = (id, apiResponse) => {
    var _a;
    replaceContentApi$1(id, apiResponse);
    (_a = document.getElementById(id)) === null || _a === void 0 ? void 0 : _a.click();
};
const replaceContentApi$1 = (popupId, apiResponse) => {
    if (!popupId || !document.getElementById(popupId))
        return;
    const str = document.getElementById(popupId).innerHTML;
    const data = (apiResponse === null || apiResponse === void 0 ? void 0 : apiResponse.data[0]) || (apiResponse === null || apiResponse === void 0 ? void 0 : apiResponse.data);
    const searchRegExp = /{{(\w*)}}/g;
    document.getElementById(popupId).innerHTML = str.replaceAll(searchRegExp, (i) => {
        return data[i.replace(/{{/, '').replace(/}}/, '')];
    });
};
const handleSuccess$1 = (popupResponse, apiResponse) => {
    if (!popupResponse)
        return;
    if (!(popupResponse === null || popupResponse === void 0 ? void 0 : popupResponse.custom.length)) {
        openPopup$1(popupResponse === null || popupResponse === void 0 ? void 0 : popupResponse.success, apiResponse);
        return;
    }
    popupResponse.custom.forEach((item) => {
        if (get(apiResponse, item.key) === item.value)
            openPopup$1(item.popup, apiResponse);
    });
};
const handleFailed$1 = (popupResponse, apiResponse) => {
    if (!popupResponse)
        return;
    openPopup$1(popupResponse.failed, apiResponse);
};
const formatData$1 = (contentType, values) => {
    let data = values || {};
    if (contentType === ContentType.formData) {
        data = new FormData();
        Object.entries(values).forEach(([key, value]) => {
            data.append(key, value);
        });
    }
    else if (contentType === ContentType.urlEncoded) {
        const body = [];
        Object.entries(values).forEach(([key, value]) => {
            const encodedKey = encodeURIComponent(key);
            const encodedValue = encodeURIComponent(value);
            body.push(`${encodedKey}=${encodedValue}`);
        });
        data = body.join('&');
    }
    return data;
};
const useFormV2 = () => {
    const handleCallAPI = async (submit, values) => {
        var _a;
        let result;
        const { method, type: submitType, response: popupResponse } = submit;
        let headers;
        try {
            headers = JSON.parse(submit.headers || '{}');
        }
        catch (err) {
            headers = {};
        }
        if (method === 'POST') {
            if (submitType === 'api') {
                const contentType = headers['Content-Type'] ||
                    submit.contentType ||
                    ContentType.urlEncoded;
                result = await api$1.post(submit.url, formatData$1(contentType, values), {
                    headers: Object.assign(Object.assign({}, (!/^multipart\//.test(contentType) && {
                        'Content-Type': contentType,
                    })), headers),
                });
            }
            if (submitType === 'googleSheet')
                result = await form.insertDataToGoogleSheet(submit.url, values, defaultGGSheetKey$1);
        }
        if (method === 'GET') {
            result = await api$1.get(submit.url, {
                params: values,
                headers,
            });
        }
        const httpCode = result === null || result === void 0 ? void 0 : result.code;
        if (method === 'GET' && !((_a = result === null || result === void 0 ? void 0 : result.data) === null || _a === void 0 ? void 0 : _a.length)) {
            handleFailed$1(popupResponse, result);
        }
        else if (httpCode === 200) {
            handleSuccess$1(popupResponse, result);
        }
        else {
            handleFailed$1(popupResponse, result);
        }
    };
    return {
        handleCallAPI,
    };
};

const getErrorMessage$1 = (type) => {
    let error = '';
    if (type === 'required')
        error = 'Không được để trống';
    if (type === 'pattern')
        error = 'Vui lòng nhập đúng định dạng';
    return error;
};
const FormV2 = React.forwardRef((props, ref) => {
    const { commonStyle, children, isEditingMode = false, submits = [], hiddenFields = [], formStyle } = props, rest = __rest(props, ["commonStyle", "children", "isEditingMode", "submits", "hiddenFields", "formStyle"]);
    const { handleSubmit, register, formState: { errors }, } = useForm$1();
    const { handleCallAPI } = useFormV2();
    const onSubmit = async (values) => {
        if (isEditingMode)
            return false;
        await Promise.all(submits.map((submit) => handleCallAPI(submit, values)));
    };
    return (jsx(Wrapper$7, Object.assign({ commonStyle: commonStyle }, { children: jsxs(Form$1, Object.assign({ formStyle: formStyle, ref: ref, onSubmit: handleSubmit(onSubmit) }, rest, { children: [React.Children.map(children, (child) => {
                    const error = errors[child.props.name];
                    return child.props.name ? (jsxs("div", { children: [React.createElement(child.type, Object.assign({}, Object.assign(Object.assign({}, child.props), { register, key: child.props.name }))), error && (jsx(ErrorMessage$1, { children: getErrorMessage$1(error.type) }))] }, child.props.name)) : (child);
                }), !!hiddenFields.length &&
                    hiddenFields.map((field) => {
                        if (!field.name)
                            return null;
                        return (jsx("input", Object.assign({ type: "hidden" }, register(field.name), { value: field.value }), field.name));
                    })] })) })));
});
const ErrorMessage$1 = styled.span `
  color: rgb(255, 77, 79);
`;
const Wrapper$7 = styled.div(({ commonStyle }) => `
  display: flex;
  width: 100%;
  ${getStyle(commonStyle)}
`);
const Form$1 = styled.form(({ formStyle }) => `
  display: flex;
  ${getStyle(formStyle)}
`);
FormV2.displayName = 'FormV2';

const api = new Api('');
const defaultGGSheetKey = {
    clientEmail: 'page-builder@pagebuilder-325814.iam.gserviceaccount.com',
    privateKey: '-----BEGIN PRIVATE KEY-----\nMIIEvAIBADANBgkqhkiG9w0BAQEFAASCBKYwggSiAgEAAoIBAQDfgDUM0KqKydMq\nlrB41+o/no2Jd+ah5bLbyez1S4Nsiz8JZ/M4iQlje+YQucIRgLP2dzRXHbTgELPl\nSoXNHHDpUks/jhngYODKBNPzGi6yQ4sCFWF+r0kDgeSP0Bf3QuVZGyDrKmWHovRx\naxKgLmMoz8EoG4056pD5GuS/5N5UgqmYevBDS/775eemmUSfmzig8mUScJnNks18\nkuqP3txhbzCOTGaRa447Vn2OxatGCyDuui+jTbbmhGLD6wWz/gB8hwdTzVx5AgWJ\nLN1Ho9YtFwJvqG+HrcQoYM8548Zx9pvYHAZo6m0lgY6TqN+Q79K5J5Z63PRZ4EuP\nAZN53mV1AgMBAAECggEAHBAAOTdzDmGYfMq2ZjnwDORmz5mpMO9DJaMlOEhuvECA\nnfj6GkFklyPtp3B3fFSdZAckIIMXnKGkQNg6VB5r0/P0BEselyR63Qq9pRLGxbXC\n3LRcDTjLkV/sF52QgMiavPUxzS1B2cW9bQc9a7TfWPa7AULyWNEJumlkyu5c9x3D\nuHuThjJgmMNZR8Yd5e4nguySfTtTUoXBPuDBH9bOmUWhkjT5sqSlinEUo7Hdmde+\nHTrqZAyMR4r5q5iYyECSXAFINeKrG75uEegNRTX+0ulg8zXn91vOvfJLOe1qGNrC\njOMlW2GtKEEaPcfNEu9WmnPtKsijNTbA9MLvcaKZ+QKBgQD5VV8HFxhHgoKIWapG\nuY0tYW5swcjBDXSDy90ZSm6HEBIOu7/Q9z188O0KZ/RsA8uKTwKTh2dQnf37fs4t\nFBkL7gmU/os4I1E6+KS95md4HJB+zATjWg4zLH2oRO3b+wlhhIVcuf6weFtKWg0C\nyyMMz4rl5q1SrUyBhUm/rVkd/QKBgQDlegR6B6h5CffEh7JUMJQJ1IQuw7E3jnS0\nTTYV8dLom5QP/lHxtXP9hGneBeEIwyrI3D3c9bTAq/4iEAWuM9I9KH9yT/E3UpYM\nw9BBIGC6B6cVV5gH/VDak1bum+cRUiabMSqfzgu5oTPol8df9z6wx42Q/MkRh07l\ny7fmEvgC2QKBgDqHJ79qVHC02h9XrPjdJZ65eEpfUti+Z9NoT4ZYSom55ANBX02W\nPADQLVR8Aow4d9cHpPsslbv+2lfm9E7woNCEGAu5lMSgxbiqFUHei/tN+T1KdvtH\ncH3iu7x8MbmpvBIAIa4amwahugxCBzvYo2mmblx5QcZel87vkthDwN+hAoGAE8JE\n3mkIZZupHN/+R+eC57HHy+sHuFvoypmXXZ5b5bF3t0TTs3dHTH35S6e/76w2Y+Mq\n5Hq3nJtyn2Y3xv8pTCDPkH03rObMir7VkLYD/iCoj1rieVWjf56OLq933vLu5g/+\ntpHcSpiJyq4++ZFlcX1NQD2z5nF7Jqg65nGdr0kCgYB23jyA0/ZJ9ohv5aUrucQZ\ndkFgs/o4F6PMx+Ro40DItCERpS1OeI1gAfy8u9SOEGDBcNDfPJZHzDn3bXQ0F6DI\nEqmwnESRAOtApHIbIRRud3sFJNBV0eWc3WFe7xLi4ex3EvAKoBkxe12j2k8zAJMs\nLms3WovJ2talTXcDHSblqA==\n-----END PRIVATE KEY-----\n',
};
const openPopup = (id, apiResponse) => {
    var _a;
    replaceContentApi(id, apiResponse);
    (_a = document.getElementById(id)) === null || _a === void 0 ? void 0 : _a.click();
};
const replaceContentApi = (popupId, apiResponse) => {
    if (!popupId || !document.getElementById(popupId))
        return;
    const str = document.getElementById(popupId).innerHTML;
    const data = apiResponse === null || apiResponse === void 0 ? void 0 : apiResponse.data;
    const searchRegExp = /{{([\w,.]*)}}/g;
    document.getElementById(popupId).innerHTML = str.replaceAll(searchRegExp, (i) => {
        return get(data, i.replace(/{{/, '').replace(/}}/, ''));
    });
};
const handleSuccess = (popupResponse, apiResponse) => {
    if (!(popupResponse === null || popupResponse === void 0 ? void 0 : popupResponse.success))
        return;
    openPopup(popupResponse === null || popupResponse === void 0 ? void 0 : popupResponse.success, apiResponse);
};
const handleFailed = (popupResponse, apiResponse) => {
    if (!(popupResponse === null || popupResponse === void 0 ? void 0 : popupResponse.failed))
        return;
    openPopup(popupResponse.failed, apiResponse);
};
const handleCustomResponse = (popupResponse, apiResponse) => {
    var _a;
    if (!((_a = popupResponse === null || popupResponse === void 0 ? void 0 : popupResponse.custom) === null || _a === void 0 ? void 0 : _a.length))
        return;
    popupResponse.custom.forEach((item) => {
        var _a, _b, _c;
        if (((_b = (_a = get(apiResponse === null || apiResponse === void 0 ? void 0 : apiResponse.data, item.key)) === null || _a === void 0 ? void 0 : _a.toString) === null || _b === void 0 ? void 0 : _b.call(_a)) === ((_c = item.value) === null || _c === void 0 ? void 0 : _c.toString())) {
            openPopup(item.popup, apiResponse);
            return;
        }
    });
};
const formatData = (contentType, values) => {
    let data = values || {};
    if (contentType === ContentType.formData) {
        data = new FormData();
        Object.entries(values).forEach(([key, value]) => {
            data.append(key, value);
        });
    }
    else if (contentType === ContentType.urlEncoded) {
        const body = [];
        Object.entries(values).forEach(([key, value]) => {
            const encodedKey = encodeURIComponent(key);
            const encodedValue = encodeURIComponent(value);
            body.push(`${encodedKey}=${encodedValue}`);
        });
        data = body.join('&');
    }
    return data;
};
const useFormV3 = () => {
    const handleCallAPI = async (submit, values) => {
        const { method, type: submitType, submitTime } = submit;
        let headers;
        try {
            headers = JSON.parse(submit.headers || '{}');
        }
        catch (err) {
            headers = {};
        }
        if (submitType === 'api') {
            if (['PATCH', 'POST'].includes(method)) {
                const contentType = headers['Content-Type'] ||
                    submit.contentType ||
                    ContentType.urlEncoded;
                return api.post(submit.url, formatData(contentType, values), {
                    headers: Object.assign(Object.assign({}, (!/^multipart\//.test(contentType) && {
                        'Content-Type': contentType,
                    })), headers),
                });
            }
            if (method === 'GET') {
                return api.get(submit.url, {
                    params: values,
                    headers,
                });
            }
        }
        if (submitType === 'googleSheet') {
            const objSubmitTime = submitTime
                ? {
                    'Thời gian gửi': new Date().toLocaleString(navigator.language),
                }
                : {};
            return form.insertDataToGoogleSheet(submit.url, Object.assign(Object.assign({}, values), objSubmitTime), defaultGGSheetKey);
        }
    };
    const handleMultiSubmit = async (submits, values, response, mainConnection) => {
        let allSubmits = [...submits];
        const mainSubmit = allSubmits === null || allSubmits === void 0 ? void 0 : allSubmits[mainConnection];
        if (!mainSubmit) {
            await Promise.all(submits.map((submit) => handleCallAPI(submit, values)));
        }
        else {
            allSubmits.splice(mainConnection, 0);
            allSubmits = [mainSubmit, ...allSubmits];
            Promise.all(submits.map(async (submit, index) => {
                if (index === 0)
                    return handleCallAPI(submit, values);
                return handleCallAPI(submit, values).catch(() => { });
            }))
                .then(([result]) => {
                if ((response === null || response === void 0 ? void 0 : response.responseType) === 'default') {
                    handleSuccess(response, result);
                }
                if ((response === null || response === void 0 ? void 0 : response.responseType) === 'custom') {
                    handleCustomResponse(response, result);
                }
            })
                .catch((err) => {
                if ((response === null || response === void 0 ? void 0 : response.responseType) === 'default') {
                    handleFailed(response, { data: (err === null || err === void 0 ? void 0 : err.response) || {} });
                }
                if ((response === null || response === void 0 ? void 0 : response.responseType) === 'custom') {
                    handleCustomResponse(response, { data: (err === null || err === void 0 ? void 0 : err.response) || {} });
                }
            });
        }
    };
    const transformValues = (values, inputs) => {
        const dateFields = inputs
            .filter((el) => el.type === 'date')
            .map((el) => el.name);
        const timeFields = inputs
            .filter((el) => el.type === 'time')
            .map((el) => el.name);
        const dateTimeFields = inputs
            .filter((el) => el.type === 'datetime-local')
            .map((el) => el.name);
        return Object.entries(values)
            .map(([k, v]) => {
            if (dateFields.includes(k) && !!v)
                return [
                    k,
                    new Date(v).toLocaleDateString(navigator.language),
                ];
            if (timeFields.includes(k) && !!v)
                return [
                    k,
                    new Date(v).toLocaleTimeString(navigator.language),
                ];
            if (dateTimeFields.includes(k) && !!v)
                return [k, new Date(v).toLocaleString(navigator.language)];
            return [k, v];
        })
            .reduce((acc, curr) => (Object.assign(Object.assign({}, acc), { [curr[0]]: curr[1] })), {});
    };
    return {
        handleMultiSubmit,
        transformValues,
    };
};

const InputTextV3 = (props) => {
    const { commonStyle, inputStyle, placeholder, register, name, id, type = 'text' } = props, rest = __rest(props, ["commonStyle", "inputStyle", "placeholder", "register", "name", "id", "type"]);
    const getPattern = (type) => {
        if (type === 'email')
            return PATTERN_VALIDATION$1.email;
        if (type === 'tel')
            return PATTERN_VALIDATION$1.phone;
    };
    return (jsx(Wrapper$6, Object.assign({ commonStyle: commonStyle, id: id }, { children: jsx(StyledInput$4, Object.assign({ inputStyle: inputStyle, placeholder: placeholder, type: type }, (register
            ? register(name, Object.assign({ pattern: getPattern(type) }, rest))
            : Object.assign({ name }, rest)))) })));
};
const Wrapper$6 = styled.div(({ commonStyle }) => `
  overflow: hidden;
  ${getStyle(commonStyle)}
`);
const StyledInput$4 = styled.input(({ inputStyle }) => `
    position: relative;
    display: inline-block;
    width: 100%;
    min-width: 100%;
    color: rgba(0, 0, 0, 0.85);
    height: 100%;
    padding: 0 0.5rem;
    &:focus,
    &-focused {
      outline: 0;
    }
    &::-moz-placeholder {
      opacity: 1;
    }

    &::placeholder {
      color: #999;
    }

    &:placeholder-shown {
      text-overflow: ellipsis;
    }
    border-style: solid;
    border-color: rgb(238, 238, 238);
    border-width: 1px;
  ${getStyle(inputStyle)}
`);
InputTextV3.displayName = 'InputTextV3';

const InputDateV3 = (props) => {
    const { commonStyle, inputStyle, placeholder, register, name, id, type } = props, rest = __rest(props, ["commonStyle", "inputStyle", "placeholder", "register", "name", "id", "type"]);
    return (jsx(Wrapper$5, Object.assign({ commonStyle: commonStyle, id: id }, { children: jsx(StyledInput$3, Object.assign({ inputStyle: inputStyle, placeholder: placeholder, type: "text" }, (register ? register(name, rest) : Object.assign({ name }, rest)), { onFocus: (e) => {
                var _a;
                e.currentTarget.type = type;
                (_a = rest.onFocus) === null || _a === void 0 ? void 0 : _a.call(rest, e);
            }, onBlur: (e) => {
                var _a;
                if (!e.target.value) {
                    e.currentTarget.type = 'text';
                }
                (_a = rest.onBlur) === null || _a === void 0 ? void 0 : _a.call(rest, e);
                if (register) {
                    register(name, rest).onBlur(e);
                }
            } })) })));
};
const Wrapper$5 = styled.div(({ commonStyle }) => `
  overflow: hidden;
  ${getStyle(commonStyle)}
`);
const StyledInput$3 = styled.input(({ inputStyle }) => `
    position: relative;
    display: inline-block;
    width: 100%;
    min-width: 100%;
    color: rgba(0, 0, 0, 0.85);
    height: 100%;
    padding: 0 0.5rem;
    &:focus,
    &-focused {
      outline: 0;
    }
    &::-moz-placeholder {
      opacity: 1;
    }

    &::placeholder {
      color: #999;
    }

    &:placeholder-shown {
      text-overflow: ellipsis;
    }
    border-style: solid;
    border-color: rgb(238, 238, 238);
    border-width: 1px;
  ${getStyle(inputStyle)}
`);
InputDateV3.displayName = 'InputDateV3';

const RadioGroupV3 = (props) => {
    const { commonStyle, inputStyle, labelStyle, register, name, id, options = [] } = props, rest = __rest(props, ["commonStyle", "inputStyle", "labelStyle", "register", "name", "id", "options"]);
    return (jsx(Wrapper$4, Object.assign({ commonStyle: commonStyle, id: id }, { children: options.map((el) => (jsxs(StyledLabelWrapper$1, Object.assign({ style: { display: 'flex', alignItems: 'center' } }, { children: [jsx(StyledInput$2, Object.assign({ inputStyle: inputStyle, value: el.value }, (register ? register(name, rest) : Object.assign({ name }, rest)), { type: "radio" })), jsx(StyledLabel$2, Object.assign({ labelStyle: labelStyle }, { children: el.label }))] }), el.value))) })));
};
const Wrapper$4 = styled.div(({ commonStyle }) => `
  display: flex;
  flex-wrap: wrap;
  overflow: hidden;
  ${getStyle(commonStyle)}
`);
const StyledLabel$2 = styled.div(({ labelStyle }) => `
  vertical-align: middle;
  margin-left: 0.25rem;
  ${getStyle(labelStyle)}
  `);
const StyledLabelWrapper$1 = styled.label `
  vertical-align: middle;
  margin-bottom: 0;
`;
const StyledInput$2 = styled.input(({ inputStyle }) => `
  ${getStyle(inputStyle)}
`);
RadioGroupV3.displayName = 'RadioGroupV3';

const InputTextareaV3 = (props) => {
    const { commonStyle, inputStyle, placeholder, register, name, id } = props, rest = __rest(props, ["commonStyle", "inputStyle", "placeholder", "register", "name", "id"]);
    return (jsx(Wrapper$3, Object.assign({ commonStyle: commonStyle, id: id }, { children: jsx(StyledInput$1, Object.assign({ inputStyle: inputStyle, placeholder: placeholder }, (register ? register(name, rest) : Object.assign({ name }, rest)))) })));
};
const Wrapper$3 = styled.div(({ commonStyle }) => `
  overflow: hidden;
  ${getStyle(commonStyle)}
`);
const StyledInput$1 = styled.textarea(({ inputStyle }) => `
    position: relative;
    display: inline-block;
    width: 100%;
    min-width: 100%;
    color: rgba(0, 0, 0, 0.85);
    height: 100%;
    padding: 0 0.5rem;
    &:focus,
    &-focused {
      outline: 0;
    }
    &::-moz-placeholder {
      opacity: 1;
    }

    &::placeholder {
      color: #999;
    }

    &:placeholder-shown {
      text-overflow: ellipsis;
    }
    border-style: solid;
    border-color: rgb(238, 238, 238);
    border-width: 1px;
  ${getStyle(inputStyle)}
`);
InputTextareaV3.displayName = 'InputTextareaV3';

const SelectBoxV3 = (props) => {
    const { commonStyle, inputStyle, placeholder, register, name, id, multiple, options = [] } = props, rest = __rest(props, ["commonStyle", "inputStyle", "placeholder", "register", "name", "id", "multiple", "options"]);
    return (jsx(Wrapper$2, Object.assign({ commonStyle: commonStyle, id: id }, { children: jsxs(StyledSelect, Object.assign({ inputStyle: inputStyle, multiple: multiple, name: name }, (register ? register(name, rest) : Object.assign({ name }, rest)), { children: [jsx("option", Object.assign({ style: { color: '#999' }, value: "", disabled: multiple, selected: !multiple }, { children: placeholder || '' })), options.map((el) => (jsx("option", Object.assign({ value: el.value }, { children: el.label }), el.value)))] })) })));
};
const Wrapper$2 = styled.div(({ commonStyle }) => `
  overflow: hidden;
  ${getStyle(commonStyle)}
`);
const StyledSelect = styled.select(({ inputStyle }) => `
    position: relative;
    display: inline-block;
    width: 100%;
    min-width: 100%;
    color: rgba(0, 0, 0, 0.85);
    height: 100%;
    padding: 0 0.5rem;
    &:focus,
    &-focused {
      outline: 0;
    }
    &::-moz-placeholder {
      opacity: 1;
    }

    &::placeholder {
      color: #999;
    }

    &:placeholder-shown {
      text-overflow: ellipsis;
    }
    border-style: solid;
    border-color: rgb(238, 238, 238);
    border-width: 1px;
  ${getStyle(inputStyle)}
`);
SelectBoxV3.displayName = 'SelectBoxV3';

const CheckboxGroupV3 = (props) => {
    const { commonStyle, inputStyle, labelStyle, register, name, id, options = [] } = props, rest = __rest(props, ["commonStyle", "inputStyle", "labelStyle", "register", "name", "id", "options"]);
    return (jsx(Wrapper$1, Object.assign({ commonStyle: commonStyle, id: id }, { children: options.map((el) => (jsxs(StyledLabelWrapper, Object.assign({ style: { display: 'flex', alignItems: 'center' } }, { children: [jsx(StyledInput, Object.assign({ inputStyle: inputStyle, value: el.value }, (register ? register(name, rest) : Object.assign({ name }, rest)), { type: "checkbox" })), jsx(StyledLabel$1, Object.assign({ labelStyle: labelStyle }, { children: el.label }))] }), el.value))) })));
};
const Wrapper$1 = styled.div(({ commonStyle }) => `
  display: flex;
  flex-wrap: wrap;
  overflow: hidden;
  ${getStyle(commonStyle)}
`);
const StyledLabel$1 = styled.div(({ labelStyle }) => `
  margin-left: 0.25rem;
  vertical-align: middle;
  ${getStyle(labelStyle)}
`);
const StyledLabelWrapper = styled.label `
  vertical-align: middle;
  margin-bottom: 0;
`;
const StyledInput = styled.input(({ inputStyle }) => `
  ${getStyle(inputStyle)}
`);
CheckboxGroupV3.displayName = 'CheckboxGroupV3';

const cx = classNames.bind({});
const getErrorMessage = (type) => {
    let error = '';
    if (type === 'required')
        error = 'Đây là thông tin bắt buộc';
    if (type === 'pattern')
        error = 'Vui lòng nhập đúng định dạng';
    return error;
};
const InnerFormV3 = React.forwardRef((props, ref) => {
    const { commonStyle, children, isEditingMode = false, submits = [], hiddenFields = [], inputs, formStyle, inputStyle, labelStyle, errorStyle, showLabel, mainConnection, response, captcha } = props, rest = __rest(props, ["commonStyle", "children", "isEditingMode", "submits", "hiddenFields", "inputs", "formStyle", "inputStyle", "labelStyle", "errorStyle", "showLabel", "mainConnection", "response", "captcha"]);
    const { handleSubmit, register, formState: { errors, isSubmitting }, reset, setValue, } = useForm$1();
    const { handleMultiSubmit, transformValues } = useFormV3();
    const recaptchaRef = useRef();
    const { executeRecaptcha } = useGoogleReCaptcha();
    const formCaptchaKey = (captcha === null || captcha === void 0 ? void 0 : captcha.requestKey) || 'g-captcha-response';
    const captchaCheckboxV2 = (captcha === null || captcha === void 0 ? void 0 : captcha.version) === 'checkboxv2' && !!(captcha === null || captcha === void 0 ? void 0 : captcha.siteKey);
    const captchaInvisibleV2 = (captcha === null || captcha === void 0 ? void 0 : captcha.version) === 'invisiblev2' && !!(captcha === null || captcha === void 0 ? void 0 : captcha.siteKey);
    const captchaInvisibleV3 = (captcha === null || captcha === void 0 ? void 0 : captcha.version) === 'invisiblev3' && !!(captcha === null || captcha === void 0 ? void 0 : captcha.siteKey);
    const [inc, setInc] = useState(0);
    useEffect(() => {
        if (!isEditingMode && captchaCheckboxV2) {
            register(formCaptchaKey, {
                required: true,
            });
        }
    }, [inc]);
    const onSubmit = async (values) => {
        if (isEditingMode || isSubmitting)
            return false;
        const newValues = Object.assign({}, values);
        try {
            if (captchaCheckboxV2) {
                if (!captcha.requestKey)
                    delete newValues[formCaptchaKey];
            }
            if (captchaInvisibleV2) {
                const token = await recaptchaRef.current.executeAsync();
                if (captcha === null || captcha === void 0 ? void 0 : captcha.requestKey) {
                    newValues[captcha.requestKey] = token;
                }
            }
            if (captchaInvisibleV3) {
                const token = await executeRecaptcha((captcha === null || captcha === void 0 ? void 0 : captcha.action) || undefined);
                if (captcha === null || captcha === void 0 ? void 0 : captcha.requestKey) {
                    newValues[captcha.requestKey] = token;
                }
            }
        }
        catch (_a) {
            alert('Xác minh danh tính không thành công');
            return;
        }
        try {
            await handleMultiSubmit(submits, transformValues(newValues, inputs), response, mainConnection);
        }
        finally {
            if (captchaCheckboxV2 || captchaInvisibleV2)
                recaptchaRef.current.reset();
            reset();
            setInc((i) => i + 1);
        }
    };
    const renderCaptchaCheckboxV2 = () => {
        var _a;
        const error = errors[formCaptchaKey];
        return (jsx(Fragment, { children: captchaCheckboxV2 && (jsxs("div", Object.assign({ style: { width: (_a = captcha === null || captcha === void 0 ? void 0 : captcha.commonStyle) === null || _a === void 0 ? void 0 : _a.width } }, { children: [jsx(ReCAPTCHA, { ref: recaptchaRef, sitekey: captcha.siteKey, onChange: (token) => setValue(formCaptchaKey, token, { shouldValidate: true }), onExpired: () => setValue(formCaptchaKey, '', { shouldValidate: true }), onErrored: () => setValue(formCaptchaKey, '', { shouldValidate: true }) }), error && (jsx(ErrorMessage, Object.assign({ errorStyle: errorStyle }, { children: getErrorMessage(error.type) })))] }))) }));
    };
    const renderCaptchaInvisible = () => {
        if (captchaInvisibleV2) {
            return (jsx(ReCAPTCHA, { ref: recaptchaRef, sitekey: captcha.siteKey, size: "invisible" }));
        }
        return null;
    };
    return (jsx(Wrapper, Object.assign({ commonStyle: commonStyle }, { children: jsxs(Form, Object.assign({ formStyle: formStyle, ref: ref, onSubmit: handleSubmit(onSubmit) }, rest, { children: [(inputs || []).map((input) => {
                    const commonStyle = Object.assign({}, (input.commonStyle || {}));
                    const width = commonStyle === null || commonStyle === void 0 ? void 0 : commonStyle.width;
                    const error = errors[input === null || input === void 0 ? void 0 : input.name];
                    commonStyle === null || commonStyle === void 0 ? true : delete commonStyle.width;
                    const renderedLabel = (jsx(Fragment, { children: showLabel && (jsxs(StyledLabel, Object.assign({ labelStyle: labelStyle, className: cx({ required: input.required }) }, { children: [(input === null || input === void 0 ? void 0 : input.label) || '', " :"] }))) }));
                    const renderedError = (jsx(Fragment, { children: error && (jsx(ErrorMessage, Object.assign({ errorStyle: errorStyle }, { children: getErrorMessage(error.type) }))) }));
                    if (['text', 'tel', 'email', 'number'].includes(input.type))
                        return (jsxs("div", Object.assign({ style: { width } }, { children: [renderedLabel, jsx(InputTextV3, Object.assign({ readOnly: isEditingMode, type: input.type, register: !isEditingMode && !!input.name && register }, input, { commonStyle: commonStyle, inputStyle: Object.assign(Object.assign({}, (inputStyle || {})), (input.inputStyle || {})) })), renderedError] }), input.name));
                    if (['date', 'time', 'datetime-local'].includes(input.type))
                        return (jsxs("div", Object.assign({ style: { width } }, { children: [renderedLabel, jsx(InputDateV3, Object.assign({ readOnly: isEditingMode, type: input.type, register: !isEditingMode && !!input.name && register }, input, { commonStyle: commonStyle, inputStyle: Object.assign(Object.assign({}, (inputStyle || {})), (input.inputStyle || {})) })), renderedError] }), input.name));
                    if (input.type === 'textarea')
                        return (jsxs("div", Object.assign({ style: { width } }, { children: [renderedLabel, jsx(InputTextareaV3, Object.assign({ readOnly: isEditingMode, register: !isEditingMode && !!input.name && register }, input, { commonStyle: commonStyle, inputStyle: Object.assign(Object.assign({}, (inputStyle || {})), (input.inputStyle || {})) })), renderedError] }), input.name));
                    if (input.type === 'select-box')
                        return (jsxs("div", Object.assign({ style: { width } }, { children: [renderedLabel, jsx(SelectBoxV3, Object.assign({ readOnly: isEditingMode, register: !isEditingMode && !!input.name && register }, input, { commonStyle: commonStyle, inputStyle: Object.assign(Object.assign({}, (inputStyle || {})), (input.inputStyle || {})) })), renderedError] }), input.name));
                    if (input.type === 'radio-group')
                        return (jsxs("div", Object.assign({ style: { width } }, { children: [renderedLabel, jsx(RadioGroupV3, Object.assign({ readOnly: isEditingMode, register: !isEditingMode && !!input.name && register }, input, { commonStyle: commonStyle, labelStyle: labelStyle, inputStyle: Object.assign(Object.assign({}, (inputStyle || {})), (input.inputStyle || {})) })), renderedError] }), input.name));
                    if (input.type === 'checkbox-group')
                        return (jsxs("div", Object.assign({ style: { width } }, { children: [renderedLabel, jsx(CheckboxGroupV3, Object.assign({ readOnly: isEditingMode, register: !isEditingMode && !!input.name && register }, input, { commonStyle: commonStyle, labelStyle: labelStyle, inputStyle: Object.assign(Object.assign({}, (inputStyle || {})), (input.inputStyle || {})) })), renderedError] }), input.name));
                    return null;
                }), renderCaptchaCheckboxV2(), renderCaptchaInvisible(), !!hiddenFields.length &&
                    hiddenFields.map((field) => {
                        if (!field.name)
                            return null;
                        return (jsx("input", Object.assign({ type: "hidden" }, register(field.name), { value: field.value }), field.name));
                    }), React.Children.map(children, (child) => {
                    return child;
                })] })) })));
});
const FormV3 = React.forwardRef((props, ref) => {
    const { captcha } = props;
    const captchav3 = (captcha === null || captcha === void 0 ? void 0 : captcha.siteKey) && (captcha === null || captcha === void 0 ? void 0 : captcha.version) === 'invisiblev3';
    if (captchav3) {
        return (jsx(GoogleReCaptchaProvider, Object.assign({ reCaptchaKey: captcha.siteKey }, { children: jsx(InnerFormV3, Object.assign({ ref: ref }, props)) })));
    }
    return jsx(InnerFormV3, Object.assign({ ref: ref }, props));
});
const ErrorMessage = styled.span(({ errorStyle }) => `
  color: rgb(255, 77, 79);
  font-size: 0.9em;
  ${getStyle(errorStyle)}
`);
const Wrapper = styled.div(({ commonStyle }) => `
  display: flex;
  width: 100%;
  ${getStyle(commonStyle)}
`);
const Form = styled.form(({ formStyle }) => `
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  width: 100%;
  ${getStyle(formStyle)}
`);
const StyledLabel = styled.label(({ labelStyle }) => `
  position: relative;
  margin-bottom: 4px;
  display: inline-flex;
  align-items: center;
  font-size: 14px;
  &.required {
    &:before {
      display: inline-block;
      margin-right: 4px;
      color: #ff4d4f;
      font-size: 14px;
      font-family: SimSun, sans-serif;
      line-height: 1;
      content: '*';
    }
  }
  ${getStyle(labelStyle)}
`);
FormV3.displayName = 'FormV3';

export { Form$2 as F, InnerFormV3 as I, StyledForm as S, StyledFormItem as a, StyledLabel$3 as b, FormPopup as c, FormV2 as d, FormV3 as e };
